import React from 'react';
import { Sparkles } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useDesign } from '../../contexts/DesignContext';

/**
 * The Studio design-language opt-in (docs/app-map/102 §1).
 *
 *   <DesignMenuItem />   avatar dropdown row (Dashboard, team-member menus)
 *   <DesignToggleRow />  Settings › Profile › Appearance, TeamMemberProfileSettings
 *
 * Both write localStorage + the profile through DesignContext, so the switch
 * agrees wherever it appears. `userId` is optional: without it the choice is
 * still applied and remembered locally, it just does not follow the user to
 * another device.
 *
 * Gated (owner, 2026-09-16, after docs/app-map/112): the switch renders only
 * for super admins (`user.is_super_admin` from /api/auth/user-info) until the
 * 112 fix list lands. Members never see it; nothing else changes.
 */

const canSeeDesignToggle = (user) => user?.is_super_admin === true;

export const DesignMenuItem = ({ user, userId, onToggled }) => {
  const { isStudio, setDesign } = useDesign();
  if (!canSeeDesignToggle(user)) return null;

  const toggle = () => {
    setDesign(isStudio ? 'classic' : 'studio', userId);
    onToggled?.(!isStudio);
  };

  return (
    <button
      onClick={toggle}
      data-testid="design-toggle-menu-item"
      role="switch"
      aria-checked={isStudio}
      aria-label={`New look: currently ${isStudio ? 'on' : 'off'}`}
      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm
                 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5
                 rounded-xl transition-all duration-150"
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/10 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-slate-600 dark:text-slate-300" />
      </div>
      <span className="font-medium">New look</span>
      <span className="ml-auto pointer-events-none">
        <Switch checked={isStudio} tabIndex={-1} aria-hidden="true" />
      </span>
    </button>
  );
};

export const DesignToggleRow = ({ user, userId, className = '' }) => {
  const { isStudio, setDesign } = useDesign();
  if (!canSeeDesignToggle(user)) return null;

  return (
    <div className={`flex items-center justify-between gap-4 ${className}`} data-testid="settings-design">
      <div>
        <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Design</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Try the new look. You can switch back any time.
        </div>
      </div>
      <Switch
        checked={isStudio}
        onCheckedChange={(on) => setDesign(on ? 'studio' : 'classic', userId)}
        aria-label="Try the new look"
        data-testid="design-toggle-switch"
      />
    </div>
  );
};

export default DesignToggleRow;
